import { useMemo, useState, useEffect } from "react";
import { useGetAllMenuItemsQuery } from "../../../RTK/API/MenuItems";
import all_brands from "../../../Assets/images/all.png";

export const useMenuData = () => {
  const { data: response, error, isLoading } = useGetAllMenuItemsQuery({});
  const data = useMemo(() => response?.data || [], [response]);

  // 🧠 Group by brand → category → items
  const groupMenuData = (list) => {
    const grouped = {};
    list.forEach((item) => {
      const brandId = item.brand?._id || "unknown";
      const categoryId = item.category?._id || "uncategorized";

      if (!grouped[brandId]) {
        grouped[brandId] = {
          brandName: item.brand?.name || "No Brand",
          brandImage: item.brand?.image,
          categories: {},
        };
      }

      if (!grouped[brandId].categories[categoryId]) {
        grouped[brandId].categories[categoryId] = {
          categoryName: item.category?.nameEN || "No Category",
          categoryImage: item.category?.image,
          items: [],
        };
      }

      grouped[brandId].categories[categoryId].items.push(item);
    });

    // Add "All" category per brand
    Object.keys(grouped).forEach((brandId) => {
      const allItems = Object.values(grouped[brandId].categories).flatMap(
        (cat) => cat.items
      );
      grouped[brandId].categories["all"] = {
        categoryName: "All",
        categoryImage: "",
        items: allItems,
      };
    });

    return grouped;
  };

  // 🟢 Add global "All" brand that contains everything
  const grouped = useMemo(() => {
    const g = groupMenuData(data);
    const allItems = data;
    const allCategoriesMap = {};

    allItems.forEach((item) => {
      const catId = item.category?._id || "uncategorized";
      if (!allCategoriesMap[catId]) {
        allCategoriesMap[catId] = {
          categoryName: item.category?.nameEN || "No Category",
          categoryImage: item.category?.image,
          items: [],
        };
      }
      allCategoriesMap[catId].items.push(item);
    });

    g["all"] = {
      brandName: "All",
      brandImage: all_brands,
      categories: {
        ...allCategoriesMap,
        all: { categoryName: "All", items: allItems },
      },
    };

    return g;
  }, [data]);

  // ✅ Memoize brandIds for stable dependencies
  const brandIds = useMemo(
    () => ["all", ...Object.keys(grouped).filter((b) => b !== "all")],
    [grouped]
  );

  // 🧭 State
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Auto-select "all" brand on mount
  useEffect(() => {
    if (brandIds.length > 0 && !selectedBrand) {
      setSelectedBrand("all");
      setSelectedCategory("all");
    }
  }, [brandIds, selectedBrand]);

  // ✅ Memoize derived categories
  const categories = useMemo(() => {
    const brand = grouped[selectedBrand] || {};
    return brand.categories || {};
  }, [grouped, selectedBrand]);

  const currentCategory = useMemo(
    () => categories[selectedCategory] || { items: [] },
    [categories, selectedCategory]
  );

  // 🔍 Filter items by search term
  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return currentCategory.items;

    const allItems = [];
    brandIds.forEach((brandId) => {
      const cats = grouped[brandId]?.categories || {};
      Object.values(cats).forEach((cat) => {
        cat.items.forEach((item) => {
          if (item.Productname?.toLowerCase().includes(term)) {
            allItems.push(item);
          }
        });
      });
    });

    // Remove duplicates
    return Array.from(new Map(allItems.map((i) => [i._id, i])).values());
  }, [searchTerm, grouped, currentCategory.items, brandIds]);

  return {
    isLoading,
    error,
    grouped,
    brandIds,
    selectedBrand,
    setSelectedBrand,
    selectedCategory,
    setSelectedCategory,
    searchTerm,
    setSearchTerm,
    categories,
    filteredItems,
  };
};
